import { Box, Grid, SelectChangeEvent } from '@mui/material';
import { isEmpty } from 'lodash-es';
import { useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useSearchParams } from 'react-router-dom';
import {
  IJob,
  IJobCandidateInteraction,
  ILocation,
  ICandidate,
  amplitude,
  ICandidateRecentConversation,
} from 'wisestep-api';
import {
  WsPagination,
  ScrollableTabs,
  TabTextVariant,
  WsListingLoader,
  IFilterList,
  WsFilterTags,
  WsTypography,
  IUserDTO,
} from 'wisestep-base';

import BulkEmail from '../../../components/BulkEmail';
import BulkSmsDrawer from '../../../components/BulkSms/BulkSmsDrawer';
import { aiContactPreferrence } from '../../../components/filters/components/AiContactAvailabilityFilter';
import WsJobsCrmSmartSourceAdvanceFilter from '../../../components/filters/jobsCrmSmartSource';
import JobsCrmSmartSourceAdvanceFiltersFormProps from '../../../components/filters/models/JobsCrmSmartSourceAdvanceFiltersFormProps';
import NoDataFoundCard from '../../../components/noDataFoundCard';
import { useUserContext } from '../../../context';
import { ITrackCvPages } from '../../../features/conversationWidget/utils/cvHelper';
import { handleCandidateDownload } from '../../../features/dataDownload/helper/PayloadHelper';
import useCvViews from '../../../features/submissions/hooks/useCvViews';
import ShareCandidateProfileEmail from '../../../features/submissions/submissionListing/components/ShareCandidateProfileEmail';
import ErrorLogger from '../../../providers/ErrorLogger';
import { success, warning } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { paginationSettingsStore } from '../../../utils/PaginationSettingStore';
import { useConversationWidgetStore } from '../../../utils/useConversationWidgetStore';
import CandidateBulkSelectionBar from '../../candidates/components/candidateBulkSelectionBar';
import { TalentPoolSearchRequest } from '../../candidates/talentpool/components/search/TalentPoolSearchRequest';
import {
  CandidateType,
  generateBackwardURL,
  getCandidateDetails,
  getInteraction,
} from '../../candidates/utils/CandidateHelper';
import {
  getDefaultAdavnacedSetFilters,
  JobsCrm,
  pushSmartSourceFilters,
  smartSourceTabsList,
} from '../components/helper/JobsCrmHelper';
import { handleSelectedCandidatesLimit } from '../components/helper/SendSmsEmailFuntionality';
import JobsCrmCard from '../components/jobsCrmCard';
import { JobsCrmDefaultRequest } from '../components/requests/JobsCrmDefaultRequest';
import JobsCrmSearch from '../components/search';
import useAISmartSourceMapped from '../hooks/useAISmartSourceMapped';
import useJobCrmListing from '../hooks/useJobCrmListing';
import {
  amplitudeEventJobCrmSmartSourceAiSuggestionsSelectUnselect,
  amplitudeEventJobCrmSmartSourceSimilarJobsSelectUnselect,
} from '../utils/AmplitudeEvents';

interface JobsCrmSmartSourceProps {
  job: IJob;
  index?: number;
}
const JobsCrmSmartSource = ({ index, job }: JobsCrmSmartSourceProps) => {
  const setCvViewSourcePage = useConversationWidgetStore((state) => state.setCvViewSourcePage);
  const paginationData = paginationSettingsStore((state) => state.getPaginationSettings);
  const paginationSettings = paginationData();
  const [openBulkSms, setOpenBulkSms] = useState<boolean>(false);
  const [openEmail, setOpenEmail] = useState<boolean>(false);
  const AiMatchingItemsPerPage = 100;
  const AiMatchingMaxResultCount = 500;
  const getLocation = (value: string): ILocation[] => {
    if (!job.locations || job.locations.length === 0) return [];

    if (value === 'Same City as Job') {
      return job?.locations.map((location) => ({
        city: location.city,
        state: location.state,
        country: location.country,
        continent: '',
        continentCode: '',
        countryCode: '',
        stateCode: '',
      }));
    } else if (value === 'Same State as Job') {
      return job.locations.map((location) => ({
        state: location.state,
        country: location.country,
        continent: '',
        continentCode: '',
        countryCode: '',
        stateCode: '',
        city: '',
      }));
    } else if (value === 'Same Country as Job') {
      return job.locations.map((location) => ({
        country: location.country,
        continent: '',
        continentCode: '',
        countryCode: '',
        stateCode: '',
        city: '',
        state: '',
      }));
    }

    return [];
  };

  const defaultRequest = { ...JobsCrmDefaultRequest };
  defaultRequest.jobIds = job.id ?? '';
  defaultRequest.jobCode = job.jobCode ?? '';
  defaultRequest.source = index == 2 ? 'AI_Suggestions' : 'AI_Suggestions_new';
  defaultRequest.sort = ['_score,desc'];
  defaultRequest.jobMatchingContent = [job?.title ?? '', job?.description ?? ''];
  defaultRequest.excludeDuplicateRecords = true;
  defaultRequest.jobTitle = job.title;
  defaultRequest.candidateSearchType = 'All';
  defaultRequest.emailAvailability = true;
  defaultRequest.candidateAllLocationFields = getLocation('Same State as Job');
  defaultRequest.skills = job.skills;
  defaultRequest.phoneAvailability = true;
  defaultRequest.minMatchScore = 90;
  defaultRequest.size =
    index == 0 ? Number(paginationSettings.jobCrmPagination ?? 20) : AiMatchingItemsPerPage;
  defaultRequest.query = '';
  const [searchParams, setSearchParams] = useSearchParams();
  const queryData = searchParams.get('d') ?? '';
  const [query, setQuery] = useState<IJobCandidateInteraction>(
    queryData ? JSON.parse(decodeURIComponent(queryData)) : defaultRequest
  );
  const setNotification = useNotificationStore((state) => state.setNotification);

  const [advancedFilterDefaultValues, setAdvancedFilterFields] =
    useState<JobsCrmSmartSourceAdvanceFiltersFormProps>(
      queryData
        ? getDefaultAdavnacedSetFilters(query, job)
        : {
            aiLocations: 'Same State as Job',
            contactAvailability: Object.values(aiContactPreferrence),
            relevance: [90, 100],
            customLocations: [],
          }
    );
  const [loader, setLoader] = useState<boolean>(false);
  const setSearchQuery = useConversationWidgetStore((state) => state.setSearchQuery);
  const [pageNumber, setPageNumber] = useState<number>((query.page ?? 0) + 1);
  const [tab, setTab] = useState<number>(0);
  const [numberOfSelectedCandidates, setNumberOfSelectedCandidates] = useState<number>(0);
  const [appliedFilters, setAppliedFilters] = useState<IFilterList[]>(
    pushSmartSourceFilters(advancedFilterDefaultValues)
  );
  const data = useJobCrmListing(query, setLoader);
  const pageChange = (event, value) => {
    const updatedQuery = { ...query };
    updatedQuery.page = value;
    setQuery(updatedQuery);
    setPageNumber(value + 1);
    setSearchParams({ d: encodeURIComponent(JSON.stringify(updatedQuery)) });
  };

  const loggedInUser = useUserContext().userDTO as IUserDTO;
  const { keys } = useCvViews(query, data.list, loggedInUser?.email ?? '');

  const search = (data: TalentPoolSearchRequest) => {
    setSearchQuery(data.query);
    const updatedQuery = { ...query };
    updatedQuery.candidateLocations = data.locations;
    updatedQuery.radiusInMiles = data.distance;
    updatedQuery.recruiterActions = data.recruiterActions;
    updatedQuery.candidateResponses = data.candidateResponses;
    updatedQuery.page = 0;
    updatedQuery.candidateName = '';
    updatedQuery.candidateEmail = '';
    updatedQuery.phoneNumber = '';
    switch (data?.searchBy) {
      case 'Name':
        updatedQuery.candidateName = data?.query.trim() ?? '';
        break;
      case 'Email':
        updatedQuery.candidateEmail = data?.query.trim() ?? '';
        break;
      case 'Phone No':
        updatedQuery.phoneNumber = data?.query.trim() ?? '';
        break;
      default:
        break;
    }
    setPageNumber(1);
    if (data.sort) updatedQuery.sort = [data.sort];
    setQuery(updatedQuery);
    setSearchParams({ d: encodeURIComponent(JSON.stringify(updatedQuery)) });
    amplitude('Smart_Source_search_button', {
      userId: loggedInUser.email,
      clickedOn: 'Search',
      pageName: 'JobCrm',
      pageUrl: window.location.href,
    });
  };
  const isAISmartSourceMappedData = useAISmartSourceMapped(data.list, job.id);

  const updateQuery = (data, reset = false) => {
    const updatedQuery = { ...query };
    updatedQuery.minMatchScore = data.relevance[0];
    if (data.contactAvailability.includes('Email')) updatedQuery.emailAvailability = true;
    else updatedQuery.emailAvailability = false;
    if (data.contactAvailability.includes('Phone')) updatedQuery.phoneAvailability = true;
    else updatedQuery.phoneAvailability = false;
    if (tab == 0)
      updatedQuery.candidateAllLocationFields =
        data.aiLocations === 'custom' ? data.customLocations : getLocation(data.aiLocations ?? '');
    updatedQuery.page = 0;
    if (reset) {
      updatedQuery.candidateName = '';
      updatedQuery.candidateEmail = '';
      updatedQuery.phoneNumber = '';
    }
    setPageNumber(1);
    setQuery(updatedQuery);
    setSearchParams({ d: encodeURIComponent(JSON.stringify(updatedQuery)) });
  };
  const onApplyFilters = (data: JobsCrmSmartSourceAdvanceFiltersFormProps, reset = false) => {
    setAdvancedFilterFields(data);
    updateQuery(data, reset);
    setAppliedFilters(pushSmartSourceFilters(data as JobsCrmSmartSourceAdvanceFiltersFormProps));
  };

  useEffect(() => {
    const redirectionFlag = searchParams.get('redirection') ?? false;
    const savedPosition = sessionStorage.getItem('scrollPosition');
    if (data.list.length > 0 && savedPosition && redirectionFlag) {
      window.scrollTo(0, parseInt(savedPosition, 10));
      sessionStorage.removeItem('scrollPosition');
    }
  }, [data.list.length, searchParams]);
  const onDeleteFilterTag = (tag: string) => {
    setAppliedFilters((prev) => {
      const idOfChip = prev.find((existChip) => existChip.tag === tag)?.id ?? '';
      const updatedQuery = { ...advancedFilterDefaultValues };
      if (idOfChip == 'aiLocations') updatedQuery.aiLocations = '';
      else if (idOfChip == 'contactAvailability') updatedQuery.contactAvailability = [];
      setAdvancedFilterFields(updatedQuery);
      updateQuery(updatedQuery);
      const index = prev.findIndex((existChip) => existChip.id === idOfChip);
      const newFilters = [...prev.slice(0, index), ...prev.slice(index + 1)];
      return newFilters;
    });
  };
  const ChangeIndex = {
    [0]: () => {
      window.history.replaceState(null, ``, `/jobs-crm/${job.id}/false/0/smartSource`);
      unselectAllCandidates();
      const updatedQuery = { ...query };
      updatedQuery.jobMatchingContent = [job?.title ?? '', job?.description ?? ''];
      updatedQuery.auditQuery = false;
      updatedQuery.searchScope = 'EVERYTHING';
      updatedQuery.source = 'AI_Suggestions_new';
      updatedQuery.excludeDuplicateRecords = true;
      updatedQuery.candidateSearchType = 'All';
      updatedQuery.targetIndex = '';
      updatedQuery.suggestedJobCode = job.jobCode;
      updatedQuery.size = Number(paginationSettings.jobCrmPagination ?? 20);
      updatedQuery.jobTitle = job?.title;
      updatedQuery.skills = job?.skills;
      updatedQuery.emailAvailability = true;
      updatedQuery.phoneAvailability = true;
      updatedQuery.candidateAllLocationFields = job.locations;
      setQuery(updatedQuery);
      setCvViewSourcePage(ITrackCvPages.JOBCRM_AI_SUGGESTIONS);
      setTab(0);
    },
  };
  const [openShareCandidateEmail, setOpenShareCandidateEmail] = useState<boolean>(false);
  const [candidateToShare, setCandidateToShare] = useState<ICandidate | null>();
  const [selectedCandidateEntitys, setSelectedCandidateEntitys] = useState<
    ICandidateRecentConversation[]
  >([]);
  const [cvIndex, setCvIndex] = useState<number | null>(null);
  const navigate = useConversationWidgetStore((state) => state.navigate);
  const setIsWidgetOpen = useConversationWidgetStore((state) => state.setIsWidgetOpen);
  const navigationState = useConversationWidgetStore((state) => state.navigationState);
  const updateNavigationState = useConversationWidgetStore((state) => state.updateNavigationState);
  const updateFromPage = useConversationWidgetStore((state) => state.updateFromPage);

  useEffect(() => {
    if (navigationState && navigate && cvIndex !== null) {
      if (cvIndex > 0 && cvIndex <= data.list.length - 1 && navigate == 'prev') {
        const index = cvIndex - 1;
        setCvIndex(index);
      } else if (cvIndex >= 0 && cvIndex < data.list.length - 1 && navigate == 'next') {
        const index = cvIndex + 1;
        setCvIndex(index);
      } else {
        setIsWidgetOpen(false);
      }
      updateNavigationState(false);
    }
  }, [
    cvIndex,
    data.list,
    navigate,
    navigationState,
    setIsWidgetOpen,
    updateNavigationState,
    updateFromPage,
  ]);

  const addSelectedCandidate = (candidate: ICandidate & { isSelected: boolean }) => {
    if (!candidate.isSelected) setNumberOfSelectedCandidates(numberOfSelectedCandidates + 1);
    else setNumberOfSelectedCandidates(numberOfSelectedCandidates - 1);
    candidate.isSelected = !candidate.isSelected;
  };

  const selectAllCandidates = () => {
    data.list
      .filter((candidate) => candidate.email && !candidate.isInDnc)
      .forEach((candidate) => {
        candidate.isSelected = true;
      });
    setNumberOfSelectedCandidates(data.list.length);
  };

  const unselectAllCandidates = () => {
    data.list.forEach((candidate) => {
      candidate.isSelected = false;
    });
    setNumberOfSelectedCandidates(0);
  };
  const onSort = (event: SelectChangeEvent) => {
    query.sort = [event.target.value ?? 'createdOn,desc'];
    setQuery({ ...query });
    setSearchParams({ d: encodeURIComponent(JSON.stringify(query)) });
  };

  const handleDownload = async (type: string) => {
    const downloadStatus = await handleCandidateDownload(type, query);
    downloadStatus == 'success'
      ? setNotification(success('Downloaded candidates'))
      : setNotification(warning('Something went wrong, Please try again.'));
  };
  const openShare = (candidate: ICandidate & { isSelected: boolean }) => {
    setOpenShareCandidateEmail(true);
    setCandidateToShare(candidate);
    setSelectedCandidateEntitys([data.stageStatus[data.list.indexOf(candidate)]]);
  };

  useEffect(() => {
    switch (index) {
      case 0:
      case 2:
        setCvViewSourcePage(ITrackCvPages.JOBCRM_AI_SUGGESTIONS);
        break;
      case 1:
        setCvViewSourcePage(ITrackCvPages.JOBCRM_SIMILAR_JOBS);
        break;
    }
  }, [index, setCvViewSourcePage]);

  const clearSearchFilters = () =>
    onApplyFilters(
      {
        aiLocations: 'Same State as Job',
        contactAvailability: Object.values(aiContactPreferrence),
        relevance: [90, 100],
        customLocations: [] as ILocation[],
      },
      true
    );

  return (
    <Box>
      <Grid container display="flex" justifyContent="space-between">
        <Grid item xs={9}>
          <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
            <Box>
              <JobsCrmSearch
                onSort={onSort}
                onSearch={(data) => search(data)}
                type={JobsCrm.SMART_SOURCE}
                job={job}
                clearAdvanceFilters={clearSearchFilters}
                handleDownload={handleDownload}
              />
              <WsFilterTags filtersList={appliedFilters} onDelete={onDeleteFilterTag} />
            </Box>
          </ErrorBoundary>
          <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
            <Box>
              <ScrollableTabs
                defaultValue={index}
                scrollList={smartSourceTabsList}
                getTabIndex={(index) => ChangeIndex[index]()}
                variant={TabTextVariant.SUB_TITLE}
              />
            </Box>
          </ErrorBoundary>
          <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
            <Box mt={1}>
              {data.pagesCount > 0 && query.source != 'AI_Suggestions' && (
                <WsPagination
                  count={Math.ceil(
                    query.source == 'AI_Suggestions_new' &&
                      Number(paginationSettings?.jobCrmPagination)
                      ? data.pagesCount / Number(paginationSettings.jobCrmPagination)
                      : 20
                  )}
                  onChange={pageChange}
                  itemsPerPage={Number(paginationSettings.jobCrmPagination)}
                  pageNumber={query.page}
                  defaultPage={pageNumber}
                  isPageCount={true}
                  type="Candidates"
                  totalCount={
                    query.source == 'AI_Suggestions_new' && data.pagesCount ? data.pagesCount : 2000
                  }
                />
              )}
              {data.pagesCount > 0 && query.source == 'AI_Suggestions' && (
                <WsPagination
                  count={Math.ceil(
                    data.pagesCount > AiMatchingMaxResultCount
                      ? AiMatchingMaxResultCount / AiMatchingItemsPerPage
                      : data.pagesCount / AiMatchingItemsPerPage
                  )}
                  onChange={pageChange}
                  itemsPerPage={AiMatchingItemsPerPage}
                  pageNumber={query.page}
                  defaultPage={pageNumber}
                  type="Candidates"
                  totalCount={
                    data.pagesCount > AiMatchingMaxResultCount
                      ? AiMatchingMaxResultCount
                      : data.pagesCount
                  }
                />
              )}
              {query.source == 'AI_Suggestions' && data.list.length > 0 && (
                <Box p={2}>
                  <WsTypography>
                    Showing upto {AiMatchingItemsPerPage} AI Matching candidates per Page
                  </WsTypography>
                </Box>
              )}
            </Box>
          </ErrorBoundary>
          <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
            {data.list.length > 0 && !loader ? (
              <Box>
                <Box>
                  {data.list?.map((candidate, index) => (
                    <Box my={2} key={index}>
                      <JobsCrmCard
                        title={candidate.name}
                        candidate={candidate}
                        detailsArray={getCandidateDetails(candidate, CandidateType.SMART_SOURCE)}
                        skillsArray={candidate.skills}
                        tagsArray={candidate.tags ?? []}
                        onActionClick={(index) => setCvIndex(index)}
                        candidateIndex={index}
                        checkBoxOnclick={() => addSelectedCandidate(candidate)}
                        isCheckBox={true}
                        job={job}
                        isSelected={candidate.isSelected}
                        interaction={getInteraction(data.stageStatus, candidate.email)}
                        amplitudeEventHandler={
                          query.source == 'AI_Suggestions' ? 'AI_Suggestions' : 'Similar_Jobs'
                        }
                        onOpenShareIcon={() => openShare(candidate)}
                        isAISmartSourceMapped={isAISmartSourceMappedData?.sourceMapped.links?.includes(
                          (candidate.email || candidate?.linkedInURL) ?? ''
                        )}
                        isCvViewed={keys?.includes(
                          (candidate.email || candidate?.linkedInURL) ?? ''
                        )}
                        backwardpathArray={[
                          { label: 'jobs', url: '/jobs' },
                          ...generateBackwardURL(job.title),
                        ]}
                      />
                    </Box>
                  ))}
                </Box>
                {openShareCandidateEmail && (
                  <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
                    <ShareCandidateProfileEmail
                      selectedCandidateEntitys={selectedCandidateEntitys}
                      candidatesList={
                        candidateToShare != null
                          ? [candidateToShare]
                          : data.list.filter(
                              (candidate) => candidate.isSelected && !candidate.isInDnc
                            )
                      }
                      open={openShareCandidateEmail}
                      handleClose={() => {
                        setOpenShareCandidateEmail(false);
                        unselectAllCandidates();
                      }}
                    />
                  </ErrorBoundary>
                )}
                <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
                  <Grid item xs={12} container justifyContent="flex-end" my={2}>
                    {data.pagesCount > 0 && query.source != 'AI_Suggestions' && (
                      <WsPagination
                        count={Math.ceil(
                          query.source == 'AI_Suggestions_new' &&
                            Number(paginationSettings?.jobCrmPagination)
                            ? data.pagesCount / Number(paginationSettings.jobCrmPagination)
                            : 20
                        )}
                        onChange={pageChange}
                        itemsPerPage={Number(paginationSettings.jobCrmPagination)}
                        pageNumber={query.page}
                        defaultPage={pageNumber}
                        isPageCount={false}
                        type="Candidates"
                        totalCount={
                          data.pagesCount > AiMatchingMaxResultCount
                            ? AiMatchingMaxResultCount
                            : data.pagesCount
                        }
                      />
                    )}
                  </Grid>
                </ErrorBoundary>
              </Box>
            ) : loader ? (
              <WsListingLoader />
            ) : (
              <NoDataFoundCard text="No Candidates Found" />
            )}
          </ErrorBoundary>
        </Grid>
        <Grid item xs={2.8}>
          <WsJobsCrmSmartSourceAdvanceFilter
            job={job}
            defaultValues={advancedFilterDefaultValues}
            onApplyFilters={(data) => {
              onApplyFilters(data);
              amplitude('Smart_Source_advance_apply_CTA', {
                userId: loggedInUser.email,
                clickedOn: 'Advance Filter Apply',
                pageName: 'JobCrm',
                pageUrl: window.location.href,
              });
            }}
          />
        </Grid>
        <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
          {numberOfSelectedCandidates > 0 && (
            <CandidateBulkSelectionBar
              selectedCandidates={numberOfSelectedCandidates}
              selectAllCandidates={selectAllCandidates}
              unselectAllCandidates={unselectAllCandidates}
              openBulkSms={() => setOpenBulkSms(true)}
              openEmail={() => setOpenEmail(true)}
              amplitudeEventHandler={(eventType: string) => {
                if (query.source === 'AI_Suggestions')
                  amplitudeEventJobCrmSmartSourceAiSuggestionsSelectUnselect[eventType](
                    loggedInUser
                  );
                else
                  amplitudeEventJobCrmSmartSourceSimilarJobsSelectUnselect[eventType](loggedInUser);
              }}
            />
          )}
        </ErrorBoundary>
        <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
          <BulkSmsDrawer
            open={openBulkSms}
            handleClose={() => {
              setOpenBulkSms(false);
              handleSelectedCandidatesLimit(data);
              setNumberOfSelectedCandidates(0);
            }}
            candidates={data.list.filter(
              (candidate) =>
                candidate.isSelected && !candidate.isInDnc && !isEmpty(candidate.phoneNumber)
            )}
            defaultJob={job}
          />
        </ErrorBoundary>
        <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
          {openEmail && (
            <BulkEmail
              ignoreDuplicateCheck={true}
              open={openEmail}
              handleClose={() => {
                setOpenEmail(false);
                handleSelectedCandidatesLimit(data);
                setNumberOfSelectedCandidates(0);
              }}
              candidates={data.list.filter(
                (candidate) =>
                  candidate.isSelected &&
                  !candidate.isInDnc &&
                  (candidate.downloadSource = 'AI_MATCHING')
              )}
              drawer={true}
              defaultJob={job}
            />
          )}
        </ErrorBoundary>
      </Grid>
    </Box>
  );
};
export default JobsCrmSmartSource;
